import api from './api';

/**
 * Client for the Invoice Overrides report — every charge somebody decided not to bill,
 * with who decided, why, and what it cost.
 *
 * NOT a view of the estimate cache. Invoice Reconciliation shows overrides sitting on
 * OPEN orders, because that is the shape of the cache; the overwhelming majority of
 * overrides are on orders that have since invoiced and left it. This report reads the
 * override table directly so an override stays visible for the rest of its life.
 *
 * The server filters on DATE and LC only. Charge, reason and who-overrode-it are
 * columns on the row, so the table filters those without a round trip — there is
 * deliberately no server-side equivalent, because two ways to ask the same question
 * would let the page's cards and its grid disagree.
 *
 * `amount` is null when the charge could not be valued at all — it is NOT zero, and
 * the two must not be conflated. Most overrides have a figure: stamped at the decision,
 * filled by the sweep, or reconstructed by scripts/backfill_override_amounts. Totals
 * count only the rows that have one, and `missing_amount_count` says how many were left
 * out. `amount_source` carries the provenance for audit; the page does not render it.
 */

/** Which date a range filters on. The question changes with the date you ask it on. */
export type OverrideDateBasis = 'override' | 'invoice' | 'delivery';

export interface InvoiceOverrideRow {
  recno: number;
  pronumber: number;
  line_item: string;
  reason: string;
  /** Additional Comments — the specifics behind the list reason. '' when none. */
  comments: string;
  /** Raw CMS username — the filter value. */
  overridden_by: string;
  /** AD display name, falling back to the username when there is no AD row. */
  overridden_by_name: string;
  overridden_at: string | null;
  /** The NET withheld: the captured charge minus what this PRO bills for it TODAY,
   *  anywhere — main invoice or accessorial. The billed half is read live, so raising an
   *  accessorial after the decision corrects this on the next load. Negative means the
   *  override PRESERVED a charge the quote does not support. Null means unrecorded,
   *  NOT $0 — pair with `amount_known` before rendering. */
  amount: number | null;
  /** What the charge itself is worth, before netting. */
  gross_amount: number | null;
  amount_known: boolean;
  qty: number | null;
  rate: number | null;
  /** 'decision' (stamped when the override was entered) or 'sweep' (backfilled). */
  amount_source: string;
  customercode: number | null;
  customer: string;
  billto: string;
  /** Raw csr username — the filter value. */
  lc: string;
  lc_name: string;
  movetype: string | null;
  invoice_date: string | null;
  invoice_total: number | null;
  is_invoiced: boolean;
  delivery_date: string | null;
  /** Extra order fields requested via `fields` land here, keyed by field name. */
  [extraField: string]: unknown;
}

export interface InvoiceOverrideTotals {
  overrides: number;
  pros: number;
  /** Sums ONLY rows with a captured amount. Read beside `missing_amount_count`. */
  amount_total: number;
  amount_known_count: number;
  missing_amount_count: number;
  invoiced_count: number;
  /** The declined money split by whether the decision can still be reversed. Rows with
   *  no amount fall out of BOTH, so these need not sum to `amount_total`… they do,
   *  since that total also counts only known amounts. */
  amount_invoiced: number;
  amount_open: number;
}

export interface InvoiceOverridesResponse {
  rows: InvoiceOverrideRow[];
  totals: InvoiceOverrideTotals;
  dateBasis: OverrideDateBasis;
  /** The extra field names the server actually resolved and returned on each row. */
  fields: string[];
  truncated: boolean;
}

export interface OverrideFilterOptions {
  /** Groups (tilde-joined member lists, isGroup) then individuals. */
  lcs: { value: string; label: string; isGroup: boolean }[];
  dateBases: OverrideDateBasis[];
}

export interface InvoiceOverridesParams {
  dateBasis?: OverrideDateBasis;
  from?: string;
  to?: string;
  lc?: string;
  /** Extra order fields added in the table's column editor, comma-separated. */
  fields?: string;
  limit?: number;
}

const invoiceOverridesService = {
  async list(params: InvoiceOverridesParams = {}): Promise<InvoiceOverridesResponse> {
    const res = await api.get<InvoiceOverridesResponse>('/invoice-overrides', { params });
    return res.data;
  },

  async filterOptions(): Promise<OverrideFilterOptions> {
    const res = await api.get<OverrideFilterOptions>('/invoice-overrides/filters');
    return res.data;
  },
};

export default invoiceOverridesService;
